import { useCallback } from 'react'
import type { JSX, KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, Dialog, Flex, Image } from '@buffer-mono/popcorn'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { useOnce } from '~publish/hooks/useOnce'

import { usePublicGraphQLApiModalTracking } from './usePublicGraphQLApiModalTracking'

import heroImage from './assets/api-launch-hero.png'

import styles from './PublicGraphQLApiModalDialog.module.css'

const BANNER_ID = 'public-graphql-api-launch-dialog'

/**
 * Accounts created on or after this date never see the announcement. Since
 * landing-page arrivals are new signups, this suppresses the modal for everyone
 * who reaches the product after launch (incl. via /launch-api and
 * /made-for/developers), leaving it for the existing user base it's meant for.
 */
const LAUNCH_DATE = new Date('2026-05-27')

/** The announcement self-retires after this date. */
const EXPIRY_DATE = new Date('2026-07-01')

const SET_UP_ROUTE = '/settings/api'
const DEVELOPER_DOCS_URL = 'https://developers.buffer.com/'

const TABBABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Radix's own Tab wraparound only fires once focus is already on the first
 * or last tabbable control (see `@radix-ui/react-focus-scope`) — it never
 * accounts for focus sitting on the dialog root itself, which is where
 * `onOpenAutoFocus` below puts it. Without this, an immediate Shift+Tab
 * right after open goes unhandled and can escape the dialog.
 */
function focusLastTabbableElement(container: HTMLElement): void {
  const candidates = container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)
  candidates[candidates.length - 1]?.focus()
}

export function PublicGraphQLApiModalDialog(): JSX.Element | null {
  const { isActive, dismiss } = useDismissableBanner(BANNER_ID, {
    accountSignUpBefore: LAUNCH_DATE,
    expiryDate: EXPIRY_DATE,
  })
  const navigate = useNavigate()
  const { trackDialogViewed, trackDialogDismissed } =
    usePublicGraphQLApiModalTracking()

  useOnce(() => {
    if (!isActive) return false
    return trackDialogViewed()
  }, [isActive, trackDialogViewed])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        dismiss()
      }
    },
    [dismiss],
  )

  const handleSetUp = useCallback(() => {
    trackDialogDismissed('set_up_now')
    dismiss()
    navigate(SET_UP_ROUTE)
  }, [trackDialogDismissed, dismiss, navigate])

  const handleLearnMore = useCallback(() => {
    trackDialogDismissed('learn_more')
    dismiss()
  }, [trackDialogDismissed, dismiss])

  if (!isActive) {
    return null
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <Dialog.Content
        size="xlarge"
        className={styles.content}
        onOpenAutoFocus={(event): void => {
          // Skip Radix's default (focusing the close button) so nothing
          // looks pressed on a dialog nobody asked to open, but still move
          // focus onto the dialog itself — leaving it unfocused entirely
          // breaks Radix's Tab trap, letting focus reach the page behind.
          event.preventDefault()
          ;(event.currentTarget as HTMLElement).focus()
        }}
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>): void => {
          if (event.key !== 'Tab' || !event.shiftKey) return
          if (event.currentTarget !== document.activeElement) return
          event.preventDefault()
          focusLastTabbableElement(event.currentTarget)
        }}
      >
        <Dialog.CloseButton
          className={styles.closeButton}
          onClick={(): void => trackDialogDismissed('close_button')}
        />
        <div className={styles.mediaContainer}>
          <Image
            className={styles.image}
            src={heroImage}
            alt="Buffer API — connect Buffer to ChatGPT, Zapier, IFTTT, Make and more"
          />
        </div>

        <Dialog.Body>
          <Flex direction="column" gap="lg" align="center">
            <Flex direction="column" gap="xs" align="center">
              <Dialog.Title size="xl" align="center">
                Buffer now works wherever you do
              </Dialog.Title>
              <Dialog.Description size="lg" className={styles.description}>
                Connect Buffer to your AI assistant, your workflows, or build
                something entirely new.
              </Dialog.Description>
            </Flex>

            <Flex gap="sm" justify="center">
              <Button
                variant="primary"
                size="large"
                className={styles.ctaButton}
                onClick={handleSetUp}
              >
                Set Up Now
              </Button>
              <Button
                variant="secondary"
                size="large"
                className={styles.ctaButton}
                as="a"
                href={DEVELOPER_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLearnMore}
              >
                Learn More
              </Button>
            </Flex>
          </Flex>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}
